import { Component, OnInit } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { MatSnackBar } from '@angular/material';

const DB_PATH = '/meta/bookingAssignments';

const OWNERS = [
  'Minnie@gofloaters.com',
  'Bhairavi@gofloaters.com',
  'Vishal@gofloaters.com',
  'Prerana@gofloaters.com',
  'Sabeha@gofloaters.com',
  'Anushka@gofloaters.com',
  'Akila@gofloaters.com',
  'Sanjay@gofloaters.com',
  'Suhitha@gofloaters.com',
  'Shyam@gofloaters.com',
];

interface Assignment {
  domain: string;   // Firebase key
  owner: string;    // email value
  _editing?: boolean;
  _draft?: string;
  _saving?: boolean;
}

@Component({
  selector: 'app-config-booking-assignments',
  templateUrl: './config-booking-assignments.component.html',
  styleUrls: ['./config-booking-assignments.component.scss']
})
export class ConfigBookingAssignmentsComponent implements OnInit {
  all: Assignment[] = [];
  filtered: Assignment[] = [];
  loading = true;
  quickSearch = '';

  owners = OWNERS;

  // Add panel
  showAddPanel = false;
  adding = false;
  newDomain = '';
  newOwner = OWNERS[0];

  constructor(private db: AngularFireDatabase, private snack: MatSnackBar) {}

  ngOnInit() {
    this.db.database.ref(DB_PATH).once('value', snapshot => {
      const val = snapshot.val() || {};
      this.all = Object.keys(val)
        .map(k => ({ domain: k, owner: val[k] }))
        .sort((a, b) => a.domain.localeCompare(b.domain));
      this.loading = false;
      this.applyFilter();
    });
  }

  applyFilter() {
    const q = this.quickSearch.trim().toLowerCase();
    this.filtered = q
      ? this.all.filter(a =>
          a.domain.toLowerCase().includes(q) ||
          a.owner.toLowerCase().includes(q))
      : [...this.all];
  }

  // ── Edit ─────────────────────────────────────────────────────────────────
  startEdit(a: Assignment) {
    a._draft = a.owner;
    a._editing = true;
  }

  cancelEdit(a: Assignment) {
    a._editing = false;
    a._draft = undefined;
  }

  async saveEdit(a: Assignment) {
    a._saving = true;
    try {
      await this.db.database.ref(`${DB_PATH}/${a.domain}`).set(a._draft);
      a.owner = a._draft;
      a._editing = false;
      a._draft = undefined;
      this.snack.open(`${a.domain} updated`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Save failed. Please try again.', 'OK', { duration: 3000 });
    }
    a._saving = false;
  }

  // ── Add ──────────────────────────────────────────────────────────────────
  openAddPanel() {
    this.newDomain = '';
    this.newOwner = OWNERS[0];
    this.showAddPanel = true;
  }

  cancelAdd() {
    this.showAddPanel = false;
  }

  async addAssignment() {
    const domain = this.newDomain.trim().toLowerCase();
    if (!domain) {
      this.snack.open('Domain is required.', 'OK', { duration: 2500 });
      return;
    }
    if (this.all.find(a => a.domain === domain)) {
      this.snack.open(`${domain} already exists. Edit it instead.`, 'OK', { duration: 3000 });
      return;
    }
    this.adding = true;
    try {
      await this.db.database.ref(`${DB_PATH}/${domain}`).set(this.newOwner);
      const entry: Assignment = { domain, owner: this.newOwner };
      this.all.push(entry);
      this.all.sort((a, b) => a.domain.localeCompare(b.domain));
      this.applyFilter();
      this.showAddPanel = false;
      this.snack.open(`${domain} added`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Add failed. Please try again.', 'OK', { duration: 3000 });
    }
    this.adding = false;
  }

  ownerInitials(email: string): string {
    return email ? email.charAt(0).toUpperCase() : '?';
  }

  ownerFirstName(email: string): string {
    return email ? email.split('@')[0] : '—';
  }

  ownerColor(email: string): string {
    const colors: { [key: string]: string } = {
      'Bhairavi': '#6366f1', 'Prerana': '#0e7490', 'Shyam': '#b45309',
      'Minnie': '#16a34a',   'Vishal': '#9333ea',  'Sabeha': '#db2777',
      'Anushka': '#0891b2',  'Akila': '#d97706',   'Sanjay': '#dc2626',
      'Suhitha': '#059669',
    };
    const first = this.ownerFirstName(email);
    return colors[first] || '#4b5563';
  }
}
